import React, { useContext, useEffect, useRef, useCallback, useMemo } from "react";
import { createPortal } from "react-dom";
import WindowLevel from "../../../../../components/SVGComponents/Tools/WindowLevel";
import studyviewerContext from "../../../../../context/studyviewer/StudyviewerContext";
import ViewportContext from "../../../../../context/studyviewer/ViewportContext";
import {
  WINDOW_LEVEL_MODALITY_LIST,
  WINDOW_LEVEL_PRESETS,
} from "../../../../../constants/Constants";
import DropDown from "../../../../../components/SVGComponents/Tools/DropDown";
import { useState } from "react";
import WindowLevelPresets from "./WindowLevelPresets";
import { StackScrollTool, WindowLevelTool } from "@cornerstonejs/tools";
import { DirectionalHint, TooltipDelay, TooltipHost } from "@fluentui/react";
import { isMobile } from "react-device-detect";
import { useTranslation } from "react-i18next";
import "../../../styles/styles.css";
import "../../../styles/ColorLUTTool.css";
import themeContext from "../../../../../context/theme/ThemeContext";
import ThumbnailContext from "../../../../../context/studyviewer/ThumbnailContext";
import userContext from "../../../../../context/users/UserContext";
import { toolTipStyles } from "../../../styles/styles";
import CustomToolTipHost from "../../../../../components/CustomToolTipHost/CustomToolTipHost";
import { getViewport } from "../../../helpers/CornerstoneViewportService";
import {
  COLORMAPS,
  applyColormapToViewport,
  resetColormapOnViewport,
} from "../../../helpers/ColorLUTService";
import ColorLUTOverlay from "./ColorLUTOverlay";
import {
  createMouseButtonAssignmentPointerDownCaptureHandler,
  suppressMouseButtonAssignmentContextMenu,
  ToolbarMouseAssignmentIndicators,
} from "./mouseButtonToolbarUtils";

const DEFAULT_COLORMAP = Object.values(COLORMAPS)[0];
const DEFAULT_OPACITY = 1;
const NONE_COLORMAP_KEY = "__none__";

// Custom events for mutual exclusivity with the Fusion tool
const FUSION_ACTIVATED_EVENT = 'viewer:fusion-activated';
const COLORLUT_ACTIVATED_EVENT = 'viewer:colorlut-activated';


const WindowLevelToolComponent = ({ toolName, tool }) => {
  const { theme } = useContext(themeContext);
  const { studyModality } = useContext(studyviewerContext);
  const { activeModality } = useContext(ThumbnailContext);
  const { username } = useContext(userContext);
  const dropDownRef = useRef(null);
  const [showDropDown, setShowDropDown] = useState(false);
  const [hovered, setHovered] = useState(false);
  const [dropDownHovered, setDropDownHovered] = useState(false);
  const {
    isMPRActive,
    activeMPRTool,
    setActiveMPRTool,
    setPreviousActiveMPRTool,
    handleDefaultToolChange,
    setBoundingBoxMode,
    setIsCropEnable,
    assignMouseButtonToAssignableTool,
    mouseButtonToolAssignments,
    activeTool,
    setActiveTool,
    setPreviousActiveTool,
    activeViewportIndex,
    viewportSeriesData,
  } = useContext(ViewportContext);
  const { t } = useTranslation();

  // Color LUT state
  const [isColorLUTActive, setIsColorLUTActive] = useState(false);
  const [colorLUTColormap, setColorLUTColormap] = useState(DEFAULT_COLORMAP);
  const [colorLUTOpacity, setColorLUTOpacity] = useState(DEFAULT_OPACITY);
  const [portalTarget, setPortalTarget] = useState(null);
  const prevViewportRef = useRef(activeViewportIndex);
  // Tracks which viewport the LUT is applied on — stays fixed after activation
  // so that controls, reset, and colormap changes always target the right viewport
  // even when the user switches to a different active viewport.
  const colorLUTAppliedViewportRef = useRef(null);

  // Always-current refs used inside event listeners (avoids stale closures)
  const isColorLUTActiveRef = useRef(isColorLUTActive);
  const activeViewportIndexRef = useRef(activeViewportIndex);
  isColorLUTActiveRef.current = isColorLUTActive;
  activeViewportIndexRef.current = activeViewportIndex;

  const handleMouseLeave = () => {
    setHovered(false);
  };

  const handleMouseOver = () => {
    setHovered(true);
  };

  const handleDropDownClick = () => {
    setShowDropDown(!showDropDown);
  };

  const changeActiveTool = (toolName) => {
    setBoundingBoxMode(false);

    setIsCropEnable(false);
    if (isMPRActive) {
      if (activeMPRTool === toolName) {
        setPreviousActiveMPRTool(activeMPRTool);
        handleDefaultToolChange();
      } else {
        setPreviousActiveMPRTool(activeMPRTool);
        assignMouseButtonToAssignableTool("primary", WindowLevelTool.toolName, true);
        setActiveMPRTool(WindowLevelTool.toolName);
      }
      return;
    }
    if (
      activeTool != WindowLevelTool.toolName &&
      WINDOW_LEVEL_MODALITY_LIST.includes(activeModality)
    ) {
      setPreviousActiveTool(activeTool);
      assignMouseButtonToAssignableTool("primary", WindowLevelTool.toolName, true);
      setActiveTool(WindowLevelTool.toolName);
    } else if (
      !WINDOW_LEVEL_MODALITY_LIST.includes(activeModality) &&
      activeTool !== toolName
    ) {
      setPreviousActiveTool(activeTool);
      assignMouseButtonToAssignableTool("primary", toolName, true);
      setActiveTool(toolName);
    } else if (activeTool == toolName) {
      setPreviousActiveTool(activeTool);
      handleDefaultToolChange();
    }
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropDownRef.current && !dropDownRef.current.contains(event.target)) {
        setShowDropDown(false);
      }
    };
    document.addEventListener("click", handleClickOutside, true);
    return () => {
      document.removeEventListener("click", handleClickOutside, true);
    };
  }, []);

  // ---- When Fusion activates on the SAME viewport, deactivate Color LUT ----
  // If fusion is applied to a different viewport, leave Color LUT intact.
  useEffect(() => {
    const handleFusionActivated = async (event) => {
      if (!isColorLUTActiveRef.current) return;
      const fusionViewportIndex = event.detail?.viewportIndex;
      const lutViewportIndex = colorLUTAppliedViewportRef.current;
      if (
        fusionViewportIndex != null &&
        lutViewportIndex != null &&
        fusionViewportIndex !== lutViewportIndex
      ) {
        return; // Fusion is on a different viewport — keep Color LUT intact
      }
      const lutVpIdx = colorLUTAppliedViewportRef.current ?? activeViewportIndexRef.current;
      try {
        const vp = await getViewport(lutVpIdx);
        if (vp) resetColormapOnViewport(vp);
      } catch { /* ignore */ }
      colorLUTAppliedViewportRef.current = null;
      setIsColorLUTActive(false);
      setColorLUTColormap(DEFAULT_COLORMAP);
      setColorLUTOpacity(DEFAULT_OPACITY);
      colorLUTBaseSeriesRef.current = null;
    };
    document.addEventListener(FUSION_ACTIVATED_EVENT, handleFusionActivated);
    return () => document.removeEventListener(FUSION_ACTIVATED_EVENT, handleFusionActivated);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ---- Portal target for Color LUT overlay ----
  // Anchored to the viewport where the LUT was applied, not the active viewport.
  // This keeps the controls visible even when the user switches to another viewport.
  useEffect(() => {
    if (isColorLUTActive && colorLUTAppliedViewportRef.current != null) {
      const el = document.getElementById(`viewport_${colorLUTAppliedViewportRef.current}`);
      setPortalTarget(el || null);
    } else {
      setPortalTarget(null);
    }
  }, [isColorLUTActive]);

  // ---- Track active viewport index changes (no longer resets Color LUT controls) ----
  useEffect(() => {
    prevViewportRef.current = activeViewportIndex;
  }, [activeViewportIndex]);

  // ---- When series changes on the viewport where LUT is applied, clear Color LUT ----
  const currentViewportSeries = viewportSeriesData?.[activeViewportIndex]?.series;
  const currentLUTViewportSeries = viewportSeriesData?.[colorLUTAppliedViewportRef.current]?.series;
  const colorLUTBaseSeriesRef = useRef(null);
  useEffect(() => {
    if (!isColorLUTActive) return;
    if (
      colorLUTBaseSeriesRef.current &&
      currentLUTViewportSeries &&
      currentLUTViewportSeries !== colorLUTBaseSeriesRef.current
    ) {
      (async () => {
        try {
          const vp = await getViewport(colorLUTAppliedViewportRef.current ?? activeViewportIndex);
          if (vp) resetColormapOnViewport(vp);
        } catch { /* ignore */ }
      })();
      setIsColorLUTActive(false);
      setColorLUTColormap(DEFAULT_COLORMAP);
      setColorLUTOpacity(DEFAULT_OPACITY);
      setPortalTarget(null);
      colorLUTBaseSeriesRef.current = null;
      colorLUTAppliedViewportRef.current = null;
    }
  }, [currentLUTViewportSeries, isColorLUTActive]);

  // ---- Color LUT handlers ----
  const handleColorLUTClick = useCallback(async () => {
    setShowDropDown(false);
    if (isColorLUTActive) {
      // Toggle off — always target the viewport where the LUT is applied
      const lutVpIdx = colorLUTAppliedViewportRef.current ?? activeViewportIndex;
      try {
        const vp = await getViewport(lutVpIdx);
        if (vp) resetColormapOnViewport(vp);
      } catch { /* ignore */ }
      colorLUTAppliedViewportRef.current = null;
      setIsColorLUTActive(false);
      setColorLUTColormap(DEFAULT_COLORMAP);
      setColorLUTOpacity(DEFAULT_OPACITY);
      colorLUTBaseSeriesRef.current = null;
    } else {
      // Toggle on — record the viewport so controls stay anchored here
      try {
        const vp = await getViewport(activeViewportIndex);
        if (vp) {
          applyColormapToViewport(vp, DEFAULT_COLORMAP, { opacity: DEFAULT_OPACITY });
          colorLUTAppliedViewportRef.current = activeViewportIndex;
          setIsColorLUTActive(true);
          setColorLUTColormap(DEFAULT_COLORMAP);
          setColorLUTOpacity(DEFAULT_OPACITY);
          colorLUTBaseSeriesRef.current = currentViewportSeries || null;
          // Notify sibling tools (e.g. Fusion) so they deactivate if on the same viewport
          document.dispatchEvent(new CustomEvent(COLORLUT_ACTIVATED_EVENT, { detail: { viewportIndex: activeViewportIndex } }));
        }
      } catch { /* ignore */ }
    }
  }, [isColorLUTActive, activeViewportIndex, currentViewportSeries]);

  // "None" drops back to plain grayscale but keeps the Color LUT tool and its
  // overlay controls active — use the panel's Close (X) button to deactivate.
  const handleOverlayColormapChange = useCallback(
    async (newColormap) => {
      const lutVpIdx = colorLUTAppliedViewportRef.current ?? activeViewportIndex;
      setColorLUTColormap(newColormap);
      try {
        const vp = await getViewport(lutVpIdx);
        if (!vp) return;
        if (newColormap === NONE_COLORMAP_KEY) {
          resetColormapOnViewport(vp);
        } else {
          applyColormapToViewport(vp, newColormap, { opacity: colorLUTOpacity });
        }
      } catch { /* ignore */ }
    },
    [activeViewportIndex, colorLUTOpacity]
  );

  const handleOverlayOpacityChange = useCallback(
    async (newOpacity) => {
      setColorLUTOpacity(newOpacity);
      // Nothing rendered to update while "None" is selected.
      if (colorLUTColormap === NONE_COLORMAP_KEY) return;
      try {
        const vp = await getViewport(colorLUTAppliedViewportRef.current ?? activeViewportIndex);
        if (vp) applyColormapToViewport(vp, colorLUTColormap, { opacity: newOpacity });
      } catch { /* ignore */ }
    },
    [activeViewportIndex, colorLUTColormap]
  );

  const handleColorLUTReset = useCallback(async () => {
    const lutVpIdx = colorLUTAppliedViewportRef.current ?? activeViewportIndex;
    try {
      const vp = await getViewport(lutVpIdx);
      if (vp) resetColormapOnViewport(vp);
    } catch { /* ignore */ }
    colorLUTAppliedViewportRef.current = null;
    setIsColorLUTActive(false);
    setColorLUTColormap(DEFAULT_COLORMAP);
    setColorLUTOpacity(DEFAULT_OPACITY);
    colorLUTBaseSeriesRef.current = null;
  }, [activeViewportIndex]);

  const generalColormapOptions = useMemo(
    () => [
      { key: NONE_COLORMAP_KEY, text: "None" },
      ...Object.entries(COLORMAPS).map(([key, value]) => ({
        key: value,
        text: key,
      })),
    ],
    []
  );

  return (
    <>
      <div
        style={{
          height: "100%",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
        }}
      >
        <div
          style={{
            height: "100%",
            position: "relative",
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
          }}
        >
          <div
            style={{
              height: "100%",
              width: `${isMobile ? "60px" : "100%"}`,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <div
              style={{
                height: isMobile ? "65%" : "100%",
                display: "flex",
                alignItems: "center",
              }}
            >
              <div
                className={
                  isMobile ? "highlight-tool-mobile" : "highlight-tool"
                }
                style={{
                  width: isMobile
                    ? WINDOW_LEVEL_MODALITY_LIST.includes(activeModality)
                      ? "45px"
                      : "35px"
                    // Wider on desktop to fit the bigger icon-arrow gap below.
                    : "55px",
                }}
              >
                <CustomToolTipHost
                  delay={TooltipDelay.zero}
                  id={"window-level"}
                  directionalHint={DirectionalHint.rightCenter}
                  styles={toolTipStyles}
                  content={isMobile ? "" : t("viewer.tools.window")}
                  className={"myToolTip"}
                >
                  <div
                    className={!isMobile ? "tool-with-mouse" : ""}
                    style={{
                      height: "100%",
                      position: "relative",
                    }}
                    onClick={() => changeActiveTool(toolName)}
                    onPointerDownCapture={createMouseButtonAssignmentPointerDownCaptureHandler(
                      WindowLevelTool.toolName,
                      assignMouseButtonToAssignableTool
                    )}
                    onContextMenu={suppressMouseButtonAssignmentContextMenu}
                    onContextMenuCapture={suppressMouseButtonAssignmentContextMenu}
                    onMouseOver={handleMouseOver}
                    onMouseLeave={handleMouseLeave}
                  >
                    <WindowLevel
                      color={
                        (isMPRActive && activeMPRTool === toolName) ||
                        (!isMPRActive && activeTool === toolName) ||
                        (!isMobile && hovered)
                          ? theme.palette.activeTool
                          : theme.palette.inactiveTool
                      }
                    />
                    {!isMobile && (
                      <ToolbarMouseAssignmentIndicators
                        toolName={WindowLevelTool.toolName}
                        assignments={mouseButtonToolAssignments}
                      />
                    )}
                  </div>
                </CustomToolTipHost>
                <div
                  style={{
                    // Wider than the Rotation/HP tools' 4px gap: on desktop this icon can
                    // show the mouse-button assignment badge (bottom-right corner, protrudes
                    // ~9px past the icon), which would otherwise overlap the arrow. The badge
                    // never renders on mobile, so mobile keeps the tighter gap.
                    marginLeft: isMobile ? "4px" : "14px",
                    display: "flex",
                    alignItems: "center",
                    height: "100%"
                  }}
                  onClick={handleDropDownClick}
                  onMouseOver={() => setDropDownHovered(true)}
                  onMouseLeave={() => setDropDownHovered(false)}
                >
                  {/* arrow points up on mobile since the dropdown opens
                      upward from the bottom toolbar (matches the other mobile tool dropdowns). */}
                  <DropDown up={isMobile}
                    color={
                      dropDownHovered || hovered
                        ? theme.palette.activeTool
                        : theme.palette.inactiveTool
                    }
                  />
                </div>
              </div>
              
            </div>
            {isMobile && (
              <span
                style={{
                  fontSize: "9px",
                  lineHeight: "100%",
                  fontWeight: "400",
                  color: "var(--mobile-color-txt)",
                }}
              >
                {t("viewer.tools.window-mobile")}
              </span>
            )}
          </div>
        </div>
        <div
          className={
            isMobile ? "dropdow-bottom-position viewer-tool-more-dropdown" : "dropdow-top-position viewer-tool-more-dropdown"
          }
          ref={dropDownRef}
          style={{
            display: `${showDropDown ? "flex" : "none"}`,
            flexDirection: "column",
            position: "absolute",
            zIndex: "999",
            boxShadow:
              "0px 0.6px 1.8px rgba(0, 0, 0, 0.1), 0px 3.2px 7.2px rgba(0, 0, 0, 0.13)",
            borderRadius: "5px",
            paddingLeft: "10px",
            paddingRight: "10px",
          }}
        >
          {WINDOW_LEVEL_MODALITY_LIST.includes(activeModality) && (
            <>
              {WINDOW_LEVEL_PRESETS.map((wl) => (
                <WindowLevelPresets key={wl.key} wl={wl} setShowDropDown={setShowDropDown} />
              ))}

              {/* Separator */}
              <div
                style={{
                  height: "1px",
                  background: "#585858",
                  margin: "4px 0",
                }}
              />
            </>
          )}

          {/* Color LUT toggle — not applicable in MPR/3D mode */}
          {!isMPRActive && (
            <div
              className="stack-scroll-dropdown-elements"
              onClick={handleColorLUTClick}
              style={{
                display: "flex",
                alignItems: "center",
                cursor: "pointer",
                paddingBottom: "4px",
                color: isColorLUTActive ? "#0085FF" : undefined,
              }}
            >
              <span>{t("viewer.tools.color-lut", "Color LUT")}</span>
            </div>
          )}
        </div>
      </div>

      {/* Color LUT viewport overlay rendered via portal */}
      {!isMPRActive && isColorLUTActive &&
        portalTarget &&
        createPortal(
          <ColorLUTOverlay
            fusionColormap={colorLUTColormap}
            fusionOpacity={colorLUTOpacity}
            onColormapChange={handleOverlayColormapChange}
            onOpacityChange={handleOverlayOpacityChange}
            onReset={handleColorLUTReset}
            colormapOptionsList={generalColormapOptions}
            resetTitle={t("viewer.tools.color-lut-remove", "Remove Color LUT")}
          />,
          portalTarget
        )}
    </>
  );
};

export default WindowLevelToolComponent;
